import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';

import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { BookmarkService } from 'src/app/services/bookmark.service';
import { AppProfileService } from 'src/app/services/profile.service';

/**
 * #652: the navbar entry point to the Merkliste, next to the cart indicator.
 *
 * Deliberately NOT gated the way `app-cart-info` is. The cart hangs on
 * `profile?.customer` because ordering needs a customer; a Merkliste is personal and
 * profile-scoped, so the same condition would hide the feature from exactly the signed-in
 * users it exists for. It shows for anyone with a profile, and for nobody without one —
 * the API is `[Authorize]` (decision 3a).
 *
 * The count comes from the id set the results grid already loads, so this costs no extra
 * request; unlike the cart badge it stays visible at zero, because an empty Merkliste
 * still has a page worth reaching (it explains what the list is for).
 */
@Component({
  imports: [RouterModule, FontAwesomeModule],
  selector: 'app-bookmark-info',
  templateUrl: './bookmark-info.component.html',
  styleUrls: ['./bookmark-info.component.css'],
})
export class BookmarkInfoComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private bookmarkService = inject(BookmarkService);
  private profileService = inject(AppProfileService);

  public readonly faBookmark = faBookmark;
  public profile: ExtendedProfile = null;
  public bookmarkCount = 0;

  ngOnInit(): void {
    this.profileService.currentProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((p) => {
        this.profile = p;
        // Only after the profile settles — ensureIdsLoaded() gives up (and stays
        // retryable) while there is none, so calling it before would waste the attempt.
        this.bookmarkService.ensureIdsLoaded();
      });

    this.bookmarkService.bookmarkedIds$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => (this.bookmarkCount = ids.size));
  }
}
